const ICON_CIRCLE = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36"><circle cx="18" cy="26.06" r="1.33" fill="currentColor"/><path fill="currentColor" d="M18 22.61a1 1 0 0 1-1-1v-12a1 1 0 1 1 2 0v12a1 1 0 0 1-1 1"/><path fill="currentColor" d="M18 34a16 16 0 1 1 16-16a16 16 0 0 1-16 16m0-30a14 14 0 1 0 14 14A14 14 0 0 0 18 4"/></svg>'

const ICON_TRIANGLE = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36"><circle cx="18" cy="26.06" r="1.33" fill="currentColor"/><path fill="currentColor" d="M18 22.61a1 1 0 0 1-1-1v-12a1 1 0 1 1 2 0v12a1 1 0 0 1-1 1"/><path fill="currentColor" d="M15.062 1.681a3.221 3.221 0 0 1 5.647.002l13.89 25.56A3.22 3.22 0 0 1 31.77 32H4.022a3.22 3.22 0 0 1-2.9-4.759zM2.88 28.198A1.22 1.22 0 0 0 4 30h27.77a1.22 1.22 0 0 0 1.071-1.803L18.954 2.642a1.22 1.22 0 0 0-2.137-.001z"/></svg>'

const ICON_SAD = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36"><path fill="currentColor" d="M18 2a16 16 0 1 0 16 16A16 16 0 0 0 18 2m0 30a14 14 0 1 1 14-14a14 14 0 0 1-14 14"/><circle cx="25.16" cy="14.28" r="1.8" fill="currentColor"/><circle cx="11.41" cy="14.28" r="1.8" fill="currentColor"/><path fill="currentColor" d="M18.16 20a9 9 0 0 0-7.33 3.78a1 1 0 1 0 1.63 1.16a7 7 0 0 1 11.31-.13a1 1 0 0 0 1.6-1.2A9 9 0 0 0 18.16 20"/></svg>'

const ICONS: Record<string, string> = {
  circle: ICON_CIRCLE,
  triangle: ICON_TRIANGLE,
  sad: ICON_SAD,
}

const STYLES = `
  *, *::before, *::after { box-sizing: border-box; }

  :host {
    display: block;
    width: 100%;
    height: 100%;
    min-height: 120px;
    overflow: hidden;
  }

  :host(:only-child) {
    height: 100vh;
    height: 100dvh;
  }

  :host([no-bg]) .container { background: transparent; }

  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    /* content-background flips with both OS and a forced w-theme-* scope
       (inherited token), so the overlay follows a forced theme too. */
    background: var(--p-content-background, #fff);
    font-family: system-ui, -apple-system, sans-serif;
  }

  .icon {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
  }

  .icon svg {
    width: 100%;
    height: 100%;
  }

  :host([severity="warning"]) .icon,
  :host([severity="warning"]) .title {
    color: var(--p-warn-500, rgb(249, 115, 22));
  }

  :host(:not([severity="warning"])) .icon,
  :host(:not([severity="warning"])) .title {
    color: var(--p-danger-500, rgb(239, 68, 68));
  }

  .title {
    font-size: 16px;
    font-weight: 600;
    text-align: center;
    padding: 0 16px;
  }

  .title:empty { display: none; }

  .message {
    font-size: 13px;
    color: var(--p-text-muted-color, #71717a);
    margin-top: 6px;
    text-align: center;
    padding: 0 16px;
    max-width: 480px;
    line-height: 1.4;
  }

  .message:empty { display: none; }

  @media (prefers-color-scheme: dark) {
    :host([severity="warning"]) .icon,
    :host([severity="warning"]) .title {
      color: var(--p-warn-400, color-mix(in srgb, rgb(249, 115, 22) 40%, white));
    }
    :host(:not([severity="warning"])) .icon,
    :host(:not([severity="warning"])) .title {
      color: var(--p-danger-400, color-mix(in srgb, rgb(239, 68, 68) 40%, white));
    }
    .message {
      color: var(--p-text-muted-color, #a1a1aa);
    }
  }
`

export class WippyErrorElement extends HTMLElement {
  static observedAttributes = ['title', 'message', 'icon', 'severity']

  private _iconEl: HTMLElement | null = null
  private _titleEl: HTMLElement | null = null
  private _messageEl: HTMLElement | null = null

  connectedCallback() {
    const shadow = this.shadowRoot ?? this.attachShadow({ mode: 'open' })
    shadow.textContent = ''

    const style = document.createElement('style')
    style.textContent = STYLES
    shadow.appendChild(style)

    const container = document.createElement('div')
    container.className = 'container'

    this._iconEl = document.createElement('div')
    this._iconEl.className = 'icon'
    this._iconEl.setAttribute('part', 'icon')

    this._titleEl = document.createElement('div')
    this._titleEl.className = 'title'
    this._titleEl.setAttribute('part', 'title')

    this._messageEl = document.createElement('div')
    this._messageEl.className = 'message'
    this._messageEl.setAttribute('part', 'message')

    this._update()

    container.append(this._iconEl, this._titleEl, this._messageEl)
    shadow.appendChild(container)
  }

  disconnectedCallback() {
    this._iconEl = null
    this._titleEl = null
    this._messageEl = null
  }

  attributeChangedCallback() {
    this._update()
  }

  private _update() {
    if (this._iconEl) {
      const iconKey = this.getAttribute('icon') ?? 'circle'
      this._iconEl.innerHTML = ICONS[iconKey] ?? ICONS.circle
    }
    if (this._titleEl)
      this._titleEl.textContent = this.getAttribute('title') ?? 'Something went wrong'
    if (this._messageEl)
      this._messageEl.textContent = this.getAttribute('message') ?? ''
  }
}
